import React, { useState, useMemo, useRef } from "react";
import {
  formatCurrency,
  formatPercentage,
  formatProfitFactor,
} from "src/util/formatters";
import { useImportStatus } from "src/context/ImportStatusContext";
import { usePrivacyMode } from "src/context/PrivacyModeContext";
import { Loader2, Lock, ChevronDown, Star } from "lucide-react";
import { getGetAppDataWithParamsQueryOptions } from "src/util/query_client";
import { Button } from "src/components/ui/button";
import { Card } from "src/components/ui/card";
import {
  SubscriptionInfo,
  TradeResponse,
} from "src/model/client.schemas";
import { TradeTagDefinition, SubscriptionTier } from "src/model_v2/client.schemas";
import { calculateAnalysisDataWithOther, AnalysisDataItem, OTHER_CATEGORY_VALUE, calculateStats } from "src/util/stats";
import { enhanceTagDefinitions, EnhancedTagDefinition } from "src/util/setups";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "src/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "src/components/ui/table";
import AttributeBarChart from "src/components/AttributeBarChart";
import CumulativeReturnChart from "src/components/CumulativeReturnChart";
import { getAppDataGetAppDataQueryOptions } from "src/model/app-data";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import { normalizeTrades } from "@/util/unifiedTradesApi";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useAccount } from "src/context/AccountContext";
import { useSuspenseQuery } from "@tanstack/react-query";
import LoadingOverlay from "src/components/LoadingOverlay";
import { filterTradesForAccount, groupTradesByDay } from "src/util/trades";
import {
  getPeriodDateRanges,
  filterItemsByPeriodSelection,
} from "src/util/period_utils";
import { useAppConfig, useUserTimezone } from "@/context/AppConfigContext";
import TooltipWrapper from "@/components/TooltipWrapper";
import PeriodSelector, { PeriodSelection } from "@/components/PeriodSelector";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_app/analysis")({
  component: AnalysisPageWrapper,
  staticData: {
    title: "Analysis",
  },
  pendingComponent: () => <LoadingOverlay isLoading={true} />,
});

const AnalysisCategorySelectionPanel = ({
  tagDefinitions,
  subscriptionInfo,
  selectedAttribute,
  onAttributeSelect,
  onPeriodSelectionChange,
  significantCategories = {},
  trades,
  showCumulativeChart,
  onCumulativeChartToggle,
}: {
  tagDefinitions: EnhancedTagDefinition[];
  subscriptionInfo: SubscriptionInfo;
  selectedAttribute: string;
  onAttributeSelect: (attribute: string) => void;
  onPeriodSelectionChange: (selection: PeriodSelection) => void;
  significantCategories?: Record<string, string[]>;
  trades: TradeResponse[];
  showCumulativeChart: boolean;
  onCumulativeChartToggle: () => void;
}) => {
  // Initialize with default "all" period
  const { current, previous } = getPeriodDateRanges("all");
  const [periodSelection, setPeriodSelection] = useState<PeriodSelection>({
    currentPeriod: current,
    previousPeriod: previous || undefined,
  });
  const userTimezone = useUserTimezone();

  const handlePeriodChange = (selection: PeriodSelection) => {
    setPeriodSelection(selection);
    onPeriodSelectionChange(selection);
  };

  // Calculate equity curve to determine if Charts section should show
  const tradesInPeriod = useMemo(() => {
    return filterItemsByPeriodSelection(trades, periodSelection.currentPeriod);
  }, [trades, periodSelection.currentPeriod]);

  const stats = useMemo(() => {
    return calculateStats(tradesInPeriod, userTimezone);
  }, [tradesInPeriod, userTimezone]);

  const hasEquityCurve = Object.keys(stats.equity_curve).length > 0;

  return (
    <>
      <Card className="card flex flex-col gap-5 p-5">
        <div className="flex flex-wrap items-center gap-4">
          <h3 className="mr-auto scroll-m-20 pb-1 text-3xl font-semibold tracking-tight first:mt-0">
            Analysis
          </h3>
          {Object.keys(significantCategories).length > 0 && (
            <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
              <Star className="h-3.5 w-3.5 fill-yellow-400 text-yellow-400" />
              <span>Statistically significant</span>
            </div>
          )}
          <PeriodSelector
            value={periodSelection}
            onChange={handlePeriodChange}
            placeholder="Period"
          />
        </div>

        {/* Breakdowns Section */}
        <div>
          <h3 className="mb-3 text-xl font-semibold">Breakdowns</h3>
        </div>
        {["general", "technical", "fundamental", "personal"]
          .filter((category) =>
            tagDefinitions.some((tagDef) => tagDef.category === category),
          )
          .map((category) => (
            <div key={category}>
              <h4 className="mb-3 text-base font-medium text-muted-foreground">
                {{
                  general: "General",
                  technical: "Technical Analysis",
                  fundamental: "Fundamental Analysis",
                  personal: "Personal",
                }[category] || "Other"}
              </h4>
              <div className="flex flex-wrap gap-3">
                {tagDefinitions
                  .filter((tagDef) => tagDef.category === category)
                  .map((tagDef) => {
                    const isProFeature =
                      tagDef.required_subscription === SubscriptionTier.Pro;
                    const hasAccess =
                      !isProFeature || subscriptionInfo.insights_unlocked;
                    const isSignificant = tagDef.tag in significantCategories;

                    return (
                      <TooltipWrapper
                        content={isSignificant
                          ? `${tagDef.description} - This category shows statistically significant patterns`
                          : tagDef.description}
                        key={tagDef.tag}
                      >
                        <Button
                          variant={
                            selectedAttribute === tagDef.tag
                              ? "secondary"
                              : "outline"
                          }
                          className={cn("rounded-lg ", selectedAttribute === tagDef.tag ? "dark:!bg-black" : "dark:!bg-medium-gray")}
                          onClick={() => onAttributeSelect(tagDef.tag)}
                        >
                          {tagDef.display_value}
                          {isSignificant && (
                            <Star className="ml-1.5 h-3.5 w-3.5 fill-yellow-400 text-yellow-400" />
                          )}
                          {isProFeature && !hasAccess && (
                            <Lock className="ml-2 h-4 w-4" />
                          )}
                        </Button>
                      </TooltipWrapper>
                    );
                  })}
              </div>
            </div>
          ))}

        {/* Charts Section */}
        {hasEquityCurve && (
          <div>
            <h3 className="mb-3 text-xl font-semibold">Charts</h3>
            <div className="flex flex-wrap gap-3">
              <Button
                variant={showCumulativeChart ? "secondary" : "outline"}
                className={cn(
                  "rounded-lg",
                  showCumulativeChart ? "dark:!bg-black" : "dark:!bg-medium-gray"
                )}
                onClick={onCumulativeChartToggle}
              >
                Cumulative Return
              </Button>
            </div>
          </div>
        )}
      </Card>
    </>
  );
};

// Available limit options for display (max 50 for performance)
// "all" means show all items (only available when total <= 50)
type LimitOption = 10 | 25 | 50 | "all";

const BASE_LIMIT_OPTIONS: { value: number; label: string }[] = [
  { value: 10, label: "Top 10" },
  { value: 25, label: "Top 25" },
  { value: 50, label: "Top 50" },
];

const MAX_DISPLAY_LIMIT = 50;

const AnalysisStatsPanel = ({
  trades,
  selectedAttribute,
  periodSelection,
  tagDefinitions,
  significantValues = [],
}: {
  trades: TradeResponse[];
  selectedAttribute: string;
  periodSelection: PeriodSelection;
  tagDefinitions: EnhancedTagDefinition[];
  significantValues?: string[];
}) => {
  const userTimezone = useUserTimezone();
  const { privacyMode } = usePrivacyMode();
  const [limit, setLimit] = useState<LimitOption>(10);

  const getAttributeDisplayName = (attribute: string): string => {
    const tagDef = tagDefinitions.find((def) => def.tag === attribute);
    return tagDef ? tagDef.display_value : attribute;
  };


  // Filter trades by the current period date range
  const tradesInPeriod = useMemo(() => {
    return filterItemsByPeriodSelection(trades, periodSelection.currentPeriod);
  }, [trades, periodSelection.currentPeriod]);

  // Calculate with selected limit
  const analysisResult = useMemo(() => {
    // When "all" is selected, use a high limit to get everything
    const effectiveLimit = limit === "all" ? 1000 : limit;
    const includeOther = limit !== "all"; // No "Other" when showing all
    return calculateAnalysisDataWithOther(
      tradesInPeriod,
      selectedAttribute,
      tagDefinitions,
      userTimezone,
      includeOther,
      effectiveLimit,
    );
  }, [tradesInPeriod, selectedAttribute, tagDefinitions, userTimezone, limit]);

  // Use expanded data when showing all, otherwise use data with "Other"
  const analysisData: AnalysisDataItem[] = limit === "all"
    ? analysisResult.expandedData
    : analysisResult.data;

  const attributeDisplayName = getAttributeDisplayName(selectedAttribute);
  const totalUniqueValues = analysisResult.expandedData.length;
  const hasMultipleValues = totalUniqueValues > 10;

  // Reset limit when attribute changes
  React.useEffect(() => {
    setLimit(10);
  }, [selectedAttribute]);

  // Handle clicking on "Other" to expand to next tier
  const handleExpandOther = () => {
    if (limit === "all") return; // Already showing all

    const currentLimit = limit as number;
    if (currentLimit < 25 && totalUniqueValues > currentLimit) {
      setLimit(totalUniqueValues <= 25 ? "all" : 25);
    } else if (currentLimit < 50 && totalUniqueValues > currentLimit) {
      setLimit(totalUniqueValues <= 50 ? "all" : 50);
    } else if (totalUniqueValues <= MAX_DISPLAY_LIMIT) {
      setLimit("all");
    }
  };

  const getExtremeValue = (key: string, type: "max" | "min") => {
    const values = analysisData
      .filter((item) => item.name !== "Unknown")
      .map((item) => Number(item[key as keyof typeof item]));
    return type === "max" ? Math.max(...values) : Math.min(...values);
  };

  // Get current limit label
  const currentLimitLabel = BASE_LIMIT_OPTIONS.find(opt => opt.value === limit)?.label ?? "Top 10";

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center justify-between">
        <h3 className="text-2xl font-semibold">
          Statistics by {attributeDisplayName}
        </h3>
        {hasMultipleValues && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="flex items-center gap-1"
              >
                {limit === "all" ? `All ${totalUniqueValues}` : `${currentLimitLabel} of ${totalUniqueValues}`}
                <ChevronDown className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {BASE_LIMIT_OPTIONS
                .filter(opt => opt.value < totalUniqueValues)
                .map((option) => (
                  <DropdownMenuItem
                    key={option.label}
                    onClick={() => setLimit(option.value as LimitOption)}
                    className={cn(limit === option.value && "bg-accent")}
                  >
                    {option.label}
                  </DropdownMenuItem>
                ))}
              {totalUniqueValues <= MAX_DISPLAY_LIMIT && (
                <DropdownMenuItem
                  onClick={() => setLimit("all")}
                  className={cn(limit === "all" && "bg-accent")}
                >
                  All ({totalUniqueValues})
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        <Card className="card p-5">
          <h4 className="mb-3 text-2xl font-medium text-black dark:text-gray3">
            Number of Trades
          </h4>
          <AttributeBarChart
            data={analysisData}
            dataKey="totalCount"
            name="Number of Trades"
            significantValues={significantValues}
          />
        </Card>
        <Card className="card p-5">
          <h4 className="mb-3 text-2xl font-medium text-black dark:text-gray3">
            Return Amount
          </h4>
          <AttributeBarChart
            data={analysisData}
            dataKey="returnAmount"
            name="Return Amount"
            formatter={formatCurrency}
            hideYAxis={privacyMode}
            privacyMode={privacyMode}
            significantValues={significantValues}
          />
        </Card>
        <Card className="card p-5">
          <h4 className="mb-3 text-2xl font-medium text-black dark:text-gray3">
            Win Rate
          </h4>
          <AttributeBarChart
            data={analysisData}
            dataKey="winRate"
            name="Win Rate"
            formatter={formatPercentage}
            significantValues={significantValues}
          />
        </Card>
        <Card className="card p-5">
          <h4 className="mb-3 text-2xl font-medium text-black dark:text-gray3">
            Profit Factor
          </h4>
          <AttributeBarChart
            data={analysisData}
            dataKey="profitFactor"
            name="Profit Factor"
            formatter={formatProfitFactor}
            significantValues={significantValues}
          />
        </Card>
      </div>
      <Card className="card w-full p-0">
        <h3 className="mb-3 p-5 text-xl font-semibold">
          Detailed Statistics{" "}
          <span className="text-gray2/40 dark:text-white/40">
            ({attributeDisplayName})
          </span>
        </h3>
        <Table className="overflow-hidden rounded-lg">
          <TableHeader>
            <TableRow className="bg-purple2/10 text-black hover:bg-purple2/10 dark:border-gray3 dark:border-opacity-5 dark:bg-medium-gray">
              <TableHead className="px-7">Metric</TableHead>
              {analysisData.map((item) => {
                const isOther = item.originalValue === OTHER_CATEGORY_VALUE;
                return (
                  <TableHead
                    key={item.name}
                    className={cn(
                      "px-7",
                      isOther && "cursor-pointer bg-purple2/20 dark:bg-purple3/20 hover:bg-purple2/30 dark:hover:bg-purple3/30"
                    )}
                    onClick={isOther ? handleExpandOther : undefined}
                  >
                    <span className="flex items-center gap-1">
                      {item.name}
                      {isOther && <ChevronDown className="h-3 w-3" />}
                    </span>
                  </TableHead>
                );
              })}
            </TableRow>
          </TableHeader>
          <TableBody>
            {[
              {
                label: "Number of Trades",
                key: "totalCount",
                highlight: "max",
                formatter: (value: number) => value.toString(),
                showInPrivacyMode: true,
              },
              {
                label: "Win Count",
                key: "winCount",
                highlight: "max",
                formatter: (value: number) => value.toString(),
                showInPrivacyMode: true,
              },
              {
                label: "Loss Count",
                key: "lossCount",
                highlight: "min",
                formatter: (value: number) => value.toString(),
                showInPrivacyMode: true,
              },
              {
                label: "Win Rate",
                key: "winRate",
                highlight: "max",
                formatter: formatPercentage,
                showInPrivacyMode: true,
              },
              {
                label: "Total Return",
                key: "returnAmount",
                highlight: "max",
                formatter: formatCurrency,
                showInPrivacyMode: false,
              },
              {
                label: "Profit Factor",
                key: "profitFactor",
                highlight: "max",
                formatter: formatProfitFactor,
                showInPrivacyMode: true,
              },
              {
                label: "Average Return per Trade",
                key: "averageReturn",
                highlight: "max",
                formatter: formatCurrency,
                showInPrivacyMode: false,
              },
              {
                label: "Average Return %",
                key: "averageReturnPct",
                highlight: "max",
                formatter: formatPercentage,
                showInPrivacyMode: true,
              },
              {
                label: "Average Win",
                key: "averageWin",
                highlight: "max",
                formatter: formatCurrency,
                showInPrivacyMode: false,
              },
              {
                label: "Average Win %",
                key: "averageWinPct",
                highlight: "max",
                formatter: formatPercentage,
                showInPrivacyMode: true,
              },
              {
                label: "Average Loss",
                key: "averageLoss",
                highlight: "min",
                formatter: formatCurrency,
                showInPrivacyMode: false,
              },
              {
                label: "Average Loss %",
                key: "averageLossPct",
                highlight: "max",
                formatter: formatPercentage,
                showInPrivacyMode: true,
              },
              {
                label: "Biggest Win",
                key: "biggestWin",
                highlight: "max",
                formatter: formatCurrency,
                showInPrivacyMode: false,
              },
              {
                label: "Biggest Loss",
                key: "biggestLoss",
                highlight: "min",
                formatter: formatCurrency,
                showInPrivacyMode: false,
              },
            ]
              .filter(
                ({ showInPrivacyMode }) => !privacyMode || showInPrivacyMode,
              )
              .map(({ label, key, formatter, highlight }) => (
                <TableRow
                  key={key}
                  className="hover:bg-accent dark:border-gray3 dark:border-opacity-5 dark:hover:bg-medium-gray"
                >
                  <TableCell className="px-7">{label}</TableCell>
                  {analysisData.map((item) => {
                    const value = item[key as keyof typeof item];
                    const isOther = item.originalValue === OTHER_CATEGORY_VALUE;
                    const isHighlighted =
                      highlight &&
                      item.name !== "Unknown" &&
                      !isOther &&
                      Number(value) ===
                        getExtremeValue(key, highlight as "max" | "min");
                    return (
                      <TableCell
                        key={item.name}
                        className={cn(
                          "px-7",
                          isHighlighted && "font-bold",
                          isOther && "cursor-pointer bg-purple2/10 dark:bg-purple3/10 hover:bg-purple2/20 dark:hover:bg-purple3/20 italic"
                        )}
                        onClick={isOther ? handleExpandOther : undefined}
                      >
                        {item[key as keyof typeof item] !== undefined
                          ? formatter(Number(value))
                          : "N/A"}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
};

const AnalysisPageContent = ({
  tagDefinitions,
  subscriptionInfo,
  trades,
  significantCategories = {},
}: {
  tagDefinitions: EnhancedTagDefinition[];
  subscriptionInfo: SubscriptionInfo;
  trades: TradeResponse[];
  significantCategories?: Record<string, string[]>;
}) => {
  const [selectedAttribute, setSelectedAttribute] = useState<string>("");
  const [showCumulativeChart, setShowCumulativeChart] = useState(false);
  // Initialize with default "all" period
  const { current, previous } = getPeriodDateRanges("all");
  const [periodSelection, setPeriodSelection] = useState<PeriodSelection>({
    currentPeriod: current,
    previousPeriod: previous || undefined,
  });
  const statsRef = useRef<HTMLDivElement>(null);
  const userTimezone = useUserTimezone();

  // Calculate equity curve and trades by date for cumulative return chart
  const tradesInPeriod = useMemo(() => {
    return filterItemsByPeriodSelection(trades, periodSelection.currentPeriod);
  }, [trades, periodSelection.currentPeriod]);

  const stats = useMemo(() => {
    return calculateStats(tradesInPeriod, userTimezone);
  }, [tradesInPeriod, userTimezone]);

  const tradesByDate = useMemo(() => {
    return groupTradesByDay(tradesInPeriod as TradeResponse[], userTimezone);
  }, [tradesInPeriod, userTimezone]);

  const hasEquityCurve = Object.keys(stats.equity_curve).length > 0;

  const handleAttributeSelect = (attribute: string) => {
    setSelectedAttribute(attribute);
    setShowCumulativeChart(false); // Hide chart when selecting a breakdown

    // Only scroll on mobile devices (width < 768px)
    if (window.innerHeight < 900) {
      // Wait for next render cycle before scrolling
      setTimeout(() => {
        statsRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 0);
    }
  };

  const handleCumulativeChartToggle = () => {
    if (showCumulativeChart) {
      setShowCumulativeChart(false);
    } else {
      setShowCumulativeChart(true);
      setSelectedAttribute(""); // Clear breakdown when showing chart
    }
  };

  const { analysisPending } = useImportStatus();

  return (
    <div className="flex flex-col gap-5">
      {analysisPending && (
        <Card className="card flex items-center gap-2 p-4 text-blue-700 dark:text-blue-300">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span>
            Your analysis is being recalculated and will be up to date in a few
            minutes.
          </span>
        </Card>
      )}
      <AnalysisCategorySelectionPanel
        tagDefinitions={tagDefinitions}
        subscriptionInfo={subscriptionInfo}
        selectedAttribute={selectedAttribute}
        onAttributeSelect={handleAttributeSelect}
        onPeriodSelectionChange={setPeriodSelection}
        significantCategories={significantCategories}
        trades={trades}
        showCumulativeChart={showCumulativeChart}
        onCumulativeChartToggle={handleCumulativeChartToggle}
      />
      {/* Cumulative Return Chart */}
      {showCumulativeChart && hasEquityCurve && (
        <Card className="card p-5">
          <h3 className="mb-4 text-2xl font-semibold">Cumulative Return</h3>
          <CumulativeReturnChart
            equityCurve={stats.equity_curve}
            tradesByDate={tradesByDate}
            height={400}
          />
        </Card>
      )}
      {selectedAttribute &&
        ((): JSX.Element => {
          const selectedTagDef = tagDefinitions.find(
            (def) => def.tag === selectedAttribute,
          );
          const isProFeature =
            selectedTagDef?.required_subscription === SubscriptionTier.Pro;
          const hasAccess = !isProFeature || subscriptionInfo.insights_unlocked;

          if (!hasAccess) {
            return (
              <Card className="card p-8">
                <div className="flex flex-col items-center gap-4 text-center">
                  <Lock className="h-12 w-12 text-muted-foreground" />
                  <p className="text-lg text-muted-foreground">
                    This analysis category is only available with a Pro
                    subscription. Upgrade to unlock detailed insights and
                    advanced analytics!
                  </p>
                  <Link to="/settings">
                    <Button variant="gradient" className="mt-10">
                      Upgrade to Pro
                    </Button>
                  </Link>
                </div>
              </Card>
            );
          }

          return trades.length > 0 ? (
            <div ref={statsRef}>
              <AnalysisStatsPanel
                trades={trades}
                selectedAttribute={selectedAttribute}
                periodSelection={periodSelection}
                tagDefinitions={tagDefinitions}
                significantValues={significantCategories[selectedAttribute] || []}
              />
            </div>
          ) : (
            <Card className="card p-5">
              <p className="text-lg text-muted-foreground">
                Add trades to unlock powerful statistics! You'll see win rates,
                profit factors, and detailed statistics broken down by your
                chosen categories. Track your trading performance and identify
                what works best for you!
              </p>
            </Card>
          );
        })()}
    </div>
  );
};

function AnalysisPageWrapper() {
  return <AnalysisPage />;
}

function AnalysisPage() {
  const { selectedAccount } = useAccount();
  const { config } = useAppConfig();
  const useV2Api = useFeatureFlag("use_v2_imports");

  // Choose query options based on feature flag
  // Analysis page needs attributes for breakdown by attribute
  const query = useV2Api
    ? getGetAppDataWithParamsQueryOptions({ include_attributes: true })
    : getAppDataGetAppDataQueryOptions();
  // @ts-ignore
  const { data: appDataResponse } = useSuspenseQuery(query);

  // Normalize trades from either API version
  const normalizedTrades = normalizeTrades(appDataResponse?.data.all_trades);
  
  // Calculate top 10 setups and enhanced tag definitions at the top level
  const accountTrades = filterTradesForAccount(
    normalizedTrades,
    selectedAccount,
  );

  if (!config) return null;

  const tagDefinitionsWithSetups = useMemo(() => {
    if (!config?.tag_definitions) return [];
    return enhanceTagDefinitions(config.tag_definitions, accountTrades);
  }, [config?.tag_definitions, accountTrades]);

  // Wait for the selected account to be set since we have accounts in the response.
  if (
    !selectedAccount &&
    (appDataResponse.data.account_data?.length ?? 0) > 0
  ) {
    return null;
  }

  // Extract significant categories from the selected account's insights
  const selectedAccountData = appDataResponse.data.account_data?.find(
    (acc) => acc.account === selectedAccount
  );
  const significantCategories = selectedAccountData?.insights?.significant_categories ?? {};

  return (
    <AnalysisPageContent
      tagDefinitions={tagDefinitionsWithSetups}
      subscriptionInfo={config.subscription_info}
      trades={accountTrades}
      significantCategories={significantCategories}
    />
  );
}
